import type { SeverityLevel } from '@microsoft/applicationinsights-web'
import { getAppInsights } from 'src/telemetryService'
import { cloneError, deriveError, getStacktraceText } from 'src/Tracking/errors'
import StackTrace from 'stacktrace-js'

export const getErrorWithRealStack = async (error: Error) => {
  const errorWithStack = cloneError(error)
  try {
    const stackFrames = await StackTrace.fromError(error)
    const stack = getStacktraceText(error, stackFrames)
    errorWithStack.stack = stack
  } catch (e) {
    console.error(e)
  }
  return errorWithStack
}

/**
 * Track an exception to Application Insights and return the trace ID.
 *
 * @param exception - The error to track
 * @param severityLevel - The severity level of the error
 * @param customProperties - Additional properties to include with the exception
 * @returns The trace ID (operation_Id in Application Insights) that can be shown to users for support purposes
 *
 * **How to find the trace ID in Application Insights:**
 *
 * The returned trace ID corresponds to `operation_Id` in Application Insights.
 * To find the exception in Azure Portal:
 *
 * 1. **Via Logs (KQL)** - Recommended:
 *    - Go to Application Insights → Logs
 *    - Run: `exceptions | where operation_Id == "your-trace-id"`
 *
 * 2. **Via Transaction Search**:
 *    - Go to Application Insights → Transaction search
 *    - Filter by "Exception" event type
 *    - Search for the trace ID or filter by operation_Id property
 *
 * 3. **Via Failures**:
 *    - Go to Application Insights → Failures
 *    - Click on an exception to see "End-to-end transaction details"
 *    - Look for the operation_Id field
 */
export const trackException = async (
  exception: unknown,
  severityLevel?: SeverityLevel,
  customProperties?: Record<string, any>
): Promise<string | undefined> => {
  const ai = getAppInsights()
  if (!ai) {
    return undefined
  }

  let error: Error | undefined
  if (exception instanceof Error) {
    error = exception
  } else {
    error = deriveError(`${exception}`, 2)
  }
  const errorWithStack = await getErrorWithRealStack(error)

  ai.trackException(
    {
      exception: errorWithStack,
      severityLevel,
    },
    { ...customProperties, href: window.location.href }
  )

  // Return the trace ID (operation_Id in App Insights) so it can be shown to the user
  return ai.context?.telemetryTrace?.traceID
}
